import { useState, useEffect } from 'react';
import API from '@/lib/api';
import IOSToggle from '@/components/IOSToggle';
import BarcodePreview from '@/components/BarcodePreview';
import { FEATURES, FEATURE_CATEGORIES, mergeFeatures } from '@/lib/features';
import { X, ArrowRight, ArrowLeft, Sparkle, CheckCircle, Barcode, Warehouse, Truck, Rocket } from '@phosphor-icons/react';

/**
 * OnboardingWizard — 5-staps welkomstscherm dat nieuwe gebruikers begeleidt.
 * Triggert automatisch wanneer `inst.onboarding_completed !== true` én feature
 * `onboarding_wizard` actief is. Later manueel oproepbaar via Instellingen.
 */

const STEPS = [
  { id: 'welcome', icon: Rocket, title: 'Welkom bij BillFac' },
  { id: 'artikelen', icon: Barcode, title: 'Artikelen & Barcodes' },
  { id: 'voorraad', icon: Warehouse, title: 'Voorraad & Dashboard' },
  { id: 'leveranciers', icon: Truck, title: 'Leveranciers & Bestellingen' },
  { id: 'klaar', icon: CheckCircle, title: 'Klaar om te starten' },
];

export default function OnboardingWizard({ onClose, onComplete, filterKeys = null }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [features, setFeatures] = useState(() => mergeFeatures({}));
  const [saving, setSaving] = useState(false);
  const isMiniTour = Array.isArray(filterKeys) && filterKeys.length > 0;

  useEffect(() => {
    setStepIdx(0);
    API.get('/instellingen').then(r => setFeatures(mergeFeatures(r.data?.features || {}))).catch(() => {});
  }, []);

  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const isLast = stepIdx === STEPS.length - 1;

  const toggle = (key) => setFeatures(p => ({ ...p, [key]: { ...p[key], actief: !p[key]?.actief } }));
  const featsByCat = (cat) => FEATURES.filter(f => f.categorie === cat && (!isMiniTour || filterKeys.includes(f.key)));

  const finish = async () => {
    setSaving(true);
    try {
      // Persisteer features + markeer features-onboarding als klaar
      await API.put('/instellingen', { features, features_onboarding_done: true });
      onComplete?.();
    } catch (e) {
      alert('Opslaan mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="onboarding-wizard">
      <div className="bg-white w-[min(780px,95vw)] max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${((stepIdx + 1) / STEPS.length) * 100}%` }}></div>
        </div>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
            <Icon size={22} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Stap {stepIdx + 1} van {STEPS.length}</div>
            <h2 className="text-lg font-bold text-gray-900">{step.title}</h2>
          </div>
          <button onClick={() => (onClose || onComplete)?.()} className="text-gray-400 hover:text-gray-900" data-testid="onboarding-close"><X size={20} /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step.id === 'welcome' && (
            <div className="p-8 text-center space-y-5">
              <Sparkle size={54} className="mx-auto text-[#002FA7]" weight="duotone" />
              <h3 className="text-2xl font-bold text-gray-900">
                {isMiniTour ? 'Korte rondleiding van de nieuwe functies' : 'Hallo! Welkom bij uw nieuwe facturatie-app'}
              </h3>
              <p className="text-sm text-gray-600 max-w-lg mx-auto leading-relaxed">
                {isMiniTour ? (
                  <>We tonen u snel de <strong>{filterKeys.length} nieuwe functies</strong> die zojuist zijn toegevoegd. U kunt ze aan/uit zetten naar wens.</>
                ) : (
                  <>In de volgende 3 stappen tonen we u de <strong>10 nieuwe functies</strong> die recent zijn toegevoegd — van barcode-scanning tot automatische voorraadbestellingen. U kunt per functie beslissen of u ze <strong>aan</strong> of <strong>uit</strong> zet. Geen zorgen — alles kan later nog gewijzigd worden via <em>Instellingen → Functies</em>.</>
                )}
              </p>
              {!isMiniTour && (
                <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto pt-2 text-[11px]">
                  <div className="p-3 border border-gray-200"><Barcode size={20} className="mx-auto text-[#002FA7] mb-1" weight="duotone" /><div className="font-semibold">Barcodes</div><div className="text-gray-500">4 functies</div></div>
                  <div className="p-3 border border-gray-200"><Warehouse size={20} className="mx-auto text-[#0A9A6B] mb-1" weight="duotone" /><div className="font-semibold">Voorraad</div><div className="text-gray-500">2 functies</div></div>
                  <div className="p-3 border border-gray-200"><Truck size={20} className="mx-auto text-[#B45309] mb-1" weight="duotone" /><div className="font-semibold">Leveranciers</div><div className="text-gray-500">4 functies</div></div>
                </div>
              )}
              <div className="text-[11px] text-gray-400 italic">⏱ Duurt ongeveer {isMiniTour ? '1' : '2'} minuten</div>
            </div>
          )}

          {(step.id === 'artikelen' || step.id === 'voorraad' || step.id === 'leveranciers') && (
            <div className="p-6 space-y-4">
              {featsByCat(step.id).length === 0 ? (
                <div className="text-center text-gray-400 italic py-8 text-sm">
                  Geen nieuwe functies in deze categorie — klik op "Volgende" om verder te gaan.
                </div>
              ) : (
                <>
              {/* Context-banner per categorie */}
              {step.id === 'artikelen' && (
                <div className="bg-[#F0F4FF] border-l-4 border-[#002FA7] p-3 text-[12px] text-gray-700 space-y-2">
                  <p>Scan, genereer en print barcodes voor uw artikelen. Ideaal voor winkels, magazijnen of B2B-groothandel.</p>
                  <div className="flex items-center gap-3 pt-1">
                    <div className="bg-white p-2 border border-gray-200"><BarcodePreview value="2012345678901" height={30} displayValue /></div>
                    <div className="text-[11px] text-gray-600">↑ Voorbeeld van een EAN-13-barcode die automatisch kan gegenereerd worden</div>
                  </div>
                </div>
              )}
              {step.id === 'voorraad' && (
                <div className="bg-green-50 border-l-4 border-[#0A9A6B] p-3 text-[12px] text-gray-700">
                  <p>Uw voorraad wordt automatisch bijgehouden bij elke verkoop. Krijg een waarschuwing op het dashboard zodra er iets bijbesteld moet worden.</p>
                  <div className="mt-2 text-[11px] text-gray-600">
                    <strong>Voorbeeld:</strong> u verkoopt 5 stuks van "Pen blauw" (voorraad 10 → 5). Min. voorraad staat op 8. → Dashboard toont waarschuwing 🟡.
                  </div>
                </div>
              )}
              {step.id === 'leveranciers' && (
                <div className="bg-amber-50 border-l-4 border-[#B45309] p-3 text-[12px] text-gray-700">
                  <p>Creëer in 30 seconden een aankoopbestelling, verstuur ze per e-mail, en laat de leverancier online bevestigen of aanpassen. Vergelijk meerdere leveranciers automatisch op prijs én levertijd.</p>
                </div>
              )}

              {/* Feature-lijst van deze categorie met toggles */}
              <div className="divide-y divide-gray-100 border border-gray-200">
                {featsByCat(step.id).map(f => {
                  const state = features[f.key] || { actief: f.default_actief };
                  return (
                    <div key={f.key} className={`p-3 flex items-start gap-3 ${!state.actief ? 'opacity-60' : ''}`} data-testid={`onboarding-feat-${f.key}`}>
                      <div className="pt-0.5"><IOSToggle checked={state.actief} onChange={() => toggle(f.key)} size="md" testid={`onboarding-toggle-${f.key}`} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-gray-900">{f.label}</div>
                          {state.actief ? <span className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5">AAN</span> : <span className="text-[10px] bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5">UIT</span>}
                        </div>
                        <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">{f.beschrijving}</p>
                        {f.voordelen && <p className="text-[11px] text-gray-500 italic mt-1">✓ {f.voordelen}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>
          )}

          {step.id === 'klaar' && (
            <div className="p-8 text-center space-y-5">
              <CheckCircle size={64} className="mx-auto text-green-600" weight="duotone" />
              <h3 className="text-2xl font-bold text-gray-900">Alles staat klaar!</h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                U heeft <strong>{Object.values(features).filter(f => f.actief).length}</strong> van de {FEATURES.length} nieuwe functies geactiveerd.
                Klik op <strong>"Opslaan en beginnen"</strong> om uw voorkeuren te bewaren.
              </p>
              <div className="bg-[#F0F4FF] border border-[#002FA7]/20 p-4 text-left max-w-md mx-auto text-[12px] space-y-1">
                <div className="font-semibold text-[#002FA7] mb-1">💡 Tips om snel te starten:</div>
                <div>• Voeg uw eerste artikel toe via <strong>Artikelen → Nieuw</strong></div>
                <div>• Open een factuur en probeer de <strong>Ctrl+K</strong> snelzoeker</div>
                <div>• Stel uw bedrijfsgegevens in via <strong>Instellingen → Bedrijf</strong></div>
                <div>• Bekijk of wijzig deze wizard later via <strong>Instellingen → Functies</strong></div>
              </div>
            </div>
          )}
        </div>

        {/* Footer met nav-knoppen */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="text-[11px] text-gray-500">
            {stepIdx > 0 && stepIdx < STEPS.length - 1 && `Stap ${stepIdx + 1} / ${STEPS.length}`}
          </div>
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <button onClick={() => setStepIdx(i => i - 1)} className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-gray-300" data-testid="onboarding-prev">
                <ArrowLeft size={13} /> Vorige
              </button>
            )}
            {!isLast ? (
              <button onClick={() => setStepIdx(i => i + 1)} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="onboarding-next">
                Volgende <ArrowRight size={13} />
              </button>
            ) : (
              <button onClick={finish} disabled={saving} className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="onboarding-finish">
                {saving ? 'Opslaan…' : <>Opslaan en beginnen <CheckCircle size={14} weight="fill" /></>}
              </button>
            )}
          </div>
        </div>

        {/* Skip-link */}
        {!isLast && (
          <div className="px-6 py-2 bg-gray-50 border-t border-gray-100 text-center">
            <button onClick={finish} className="text-[11px] text-gray-400 hover:text-gray-700 hover:underline" data-testid="onboarding-skip">
              Overslaan en later bekijken via Instellingen → Functies
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
